import client from '@/shared/apis/client'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'

import { type HomecarePatient, type HomecareRecipient } from './homecareApi'

// 計画書 (薬学的管理指導計画書) の 1 版。見直すたびに新しい版ができ、直せるのは最新の版だけ。
// fields は見出しごとの行 (報告書と同じ形)。scheduled_dates は予定日 (ISO の日付・昇順)。
// yakureki-back log/263・design-book ADR-0058
export interface CarePlan {
  id: string
  version: number
  fields: Record<string, SoapContentItem[]>
  scheduled_dates: string[]
  generated_at: string | null
  edited_at: string | null
  created_at: string
  // 文を書いている途中 (作った直後・見直した直後の数十秒)。その間は取り直す
  is_generating: boolean
}

// 患者のページの計画書の行 (最新の版)。review_due = 最新の版から 1 か月過ぎた
export interface CarePlanSummary {
  id: string
  version: number
  created_at: string
  scheduled_dates: string[]
  is_generating: boolean
  review_due: boolean
}

// 計画書を出すときの頭書きと足の材料 (報告書と同じく、出すときに宛先ごとに組む)
export interface CarePlanLetterhead {
  patient: Pick<HomecarePatient, 'id' | 'name' | 'birth_date' | 'care_level'>
  recipients: HomecareRecipient[]
  pharmacy: { name: string; phone_number: string }
}

export interface CarePlans {
  // 新しい順。先頭が最新の版
  plans: CarePlan[]
  letterhead: CarePlanLetterhead
}

const plansUrl = (patientId: string) =>
  `/api/home-care/patients/${patientId}/plans/`

export const getCarePlans = async (patientId: string): Promise<CarePlans> =>
  (await client.get<CarePlans>(plansUrl(patientId))).data

// 最初の版を作る。memo = 医師の指示や聞き取ったこと (任意)。文は back が裏で書く
export const createCarePlan = async (
  patientId: string,
  memo: string
): Promise<CarePlan> =>
  (await client.post<CarePlan>(plansUrl(patientId), { memo })).data

// 見直しの版を作る (訪問頻度は引き継ぎ、予定日は案が入る)
export const reviewCarePlan = async (patientId: string): Promise<CarePlan> =>
  (await client.post<CarePlan>(`${plansUrl(patientId)}review/`)).data

// 最新の版を直す。fields は送ったキーだけ差し替わる。scheduled_dates は並びごと
// (今日より前の日は足すことも消すこともできない。back が 400 を返す)
export const patchCarePlan = async (
  planId: string,
  input: {
    fields?: Record<string, SoapContentItem[]>
    scheduled_dates?: string[]
  }
): Promise<CarePlan> =>
  (await client.patch<CarePlan>(`/api/home-care/plans/${planId}/`, input)).data
